import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Navbar } from '@/components/Navbar'
import {
  type BadgeProps,
  type BreadcrumbItem,
  BreadcrumbsCarousel,
  type ButtonProps,
  ScrollFade,
} from '@valiant/ui-shared'
import { environment } from '@/environment'
import { useNavigate } from 'react-router-dom'
import { SubBar } from '@valiant/ui-shared'

interface MainLayoutProps {
  children: ReactNode
  className?: string
  contentClassName?: string
  breadcrumbs?: BreadcrumbItem[]
  modalsComponent?: ReactNode
  title?: string
  headerTitle?: string
  headerImage?: string | React.ReactNode
  badges?: BadgeProps[]
  buttons?: ButtonProps[]
  hideBack?: boolean
  subBarOverride?: ReactNode
}

export function MainLayout({
  children,
  className,
  contentClassName,
  breadcrumbs = [],
  modalsComponent,
  hideBack = false,
  title,
  headerTitle,
  headerImage,
  badges,
  buttons,
  subBarOverride,
}: MainLayoutProps) {
  const navigate = useNavigate()
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 0)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const finalBreadcrumbs = useMemo(() => {
    const lastItem = breadcrumbs[breadcrumbs.length - 1]
    const enhancedBreadcrumbs =
      breadcrumbs.length < 1
        ? breadcrumbs
        : [
            ...breadcrumbs.slice(0, breadcrumbs.length - 1),
            {
              ...lastItem,
              badges: badges || lastItem.badges,
              buttons: buttons || lastItem.buttons,
            },
          ]
    const logoUrl = (
      environment.MSB_LOGO ||
      `/${environment.MSB}.svg` ||
      '/local.svg'
    ).toLowerCase()
    return [
      { label: environment.MSB, onClick: () => navigate('/'), imageUrl: logoUrl },
      ...enhancedBreadcrumbs,
    ]
  }, [breadcrumbs, navigate, badges, buttons])

  const subBar = useMemo(() => {
    if (subBarOverride) {
      return subBarOverride
    }

    if (!title && !headerTitle) return null

    return (
      <SubBar
        title={title}
        headerTitle={headerTitle}
        headerImage={headerImage}
        badges={badges}
        buttons={buttons}
      />
    )
  }, [title, headerTitle, headerImage, badges, buttons, subBarOverride])

  return (
    <div className={`flex min-h-screen w-full ${className ?? ''}`.trim()}>
      <div className="flex flex-col w-full">
        {/* Mobile: sticky header with navbar + breadcrumbs */}
        <ScrollFade isScrolled={isScrolled} className="md:hidden">
          <Navbar breadcrumbs={breadcrumbs} hideBack={hideBack} />
          <div className="block">
            <BreadcrumbsCarousel breadcrumbs={finalBreadcrumbs || []} />
          </div>

          {subBarOverride ? (
            <div className="mx-4 md:block md:mx-14 md:mt-4">{subBarOverride}</div>
          ) : null}
        </ScrollFade>

        {/* Desktop: navbar sticky, subbar normal flow */}
        <ScrollFade isScrolled={isScrolled} className="hidden md:block">
          <Navbar breadcrumbs={breadcrumbs} hideBack={hideBack} />
        </ScrollFade>

        {subBar ? <div className="mx-4 hidden md:block md:mx-14 md:mt-4 mb-2">{subBar}</div> : null}
        <main className={`flex-1 space-y-8 mx-4 md:mx-14 mt-4 ${contentClassName ?? ''}`.trim()}>
          {children}
        </main>
      </div>
      {modalsComponent ? modalsComponent : null}
    </div>
  )
}
